import Grid from '@mui/material/Grid';
import { Breakpoint, Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { NavItem } from '@qpr/components/Expandable/nav-items-view-model';
import React from 'react';
import { LandingCardProps } from './LandingPageCard';

type BreakpointOrNull = Breakpoint | null;

/**
 * Be careful using this hook. It only works because the number of
 * breakpoints in theme is static. It will break once you change the number of
 * breakpoints. See https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level
 */
function useWidth() {
  const theme: Theme = useTheme();
  const keys: readonly Breakpoint[] = [...theme.breakpoints.keys].reverse();
  return (
    keys.reduce((output: BreakpointOrNull, key: Breakpoint) => {
      const matches = useMediaQuery(theme.breakpoints.up(key));
      return !output && matches ? key : output;
    }, null) || 'xs'
  );
}

export type TypeGridSizes = {
  xs: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  sm: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  md: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  lg: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  xl: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
};

export const GridSizesDefault: TypeGridSizes = {
  xs: 12,
  sm: 6,
  md: 4,
  lg: 3,
  xl: 2
};

export interface LandingPageGridProps {
  sections: NavItem[];
  tenantSymbol?: string;
  sizes?: TypeGridSizes;
  card: (props: LandingCardProps) => JSX.Element;
}

const LandingPageGrid = ({ tenantSymbol = '', sections, sizes, card }: LandingPageGridProps) => {
  const width = useWidth();
  const gridSize = (sizes && sizes[width]) || GridSizesDefault[width];
  const cardsperrow = 12 / gridSize;
  const isFirst = (idx: number) => cardsperrow === 1 || idx % cardsperrow === 1;
  const isLast = (idx: number) => idx === sections.length || !(idx % cardsperrow);
  const Card = card;
  return (
    <Grid
      container
      spacing={0}
      alignContent="center"
      alignItems="center"
      style={{ marginTop: '5%' }}
    >
      {sections &&
        // eslint-disable-next-line @typescript-eslint/no-shadow
        sections.map((card: NavItem, idx: number) => (
          <Grid
            item
            key={`card${card._id}`}
            xs={(sizes && sizes.xs) || GridSizesDefault.xs}
            sm={(sizes && sizes.sm) || GridSizesDefault.sm}
            md={(sizes && sizes.md) || GridSizesDefault.md}
            lg={(sizes && sizes.lg) || GridSizesDefault.lg}
            xl={(sizes && sizes.xl) || GridSizesDefault.xl}
          >
            <Card
              data-testid={`card${card._id}`}
              name={card.title}
              link={card.link}
              image={typeof card.icon === 'string' ? card.icon : ''}
              enabled={card.enabled}
              tenant={tenantSymbol}
              orderingNumber={idx + 1}
              first={isFirst(idx + 1)}
              last={isLast(idx + 1)}
            />
          </Grid>
        ))}
    </Grid>
  );
};

export default LandingPageGrid;
